import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import api from "../../services/api";
import { Panel, Badge, thCls, tdCls, inputCls, selectCls, Field, btnPrimary, btnGhost, cls, can } from "../ui";
import { Shield, Search, BellPlus, Trash2, FileWarning } from "lucide-react";

const U = "/ujms";
const dark = "bg-[#101a2b] border border-[#26375a] rounded-sm text-slate-100";

function Kpi({ label, value, accent }) {
  return (
    <div className={`${dark} p-3`} data-testid={`le-kpi-${label.toLowerCase().replace(/ /g, "-")}`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div>
      <div className={cls("text-2xl font-bold font-mono", accent || "text-white")}>{value}</div>
    </div>
  );
}

export default function LERecords() {
  const { access } = useOutletContext();
  const navigate = useNavigate();
  const [dash, setDash] = useState(null);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("ALL");
  const [f, setF] = useState({ incident_type: "THEFT", occurred_at: new Date().toISOString().slice(0, 10), location: "", narrative: "", victim: "", suspect: "", plate: "" });
  const [searches, setSearches] = useState([]);
  const [ss, setSs] = useState({ name: "", query: "" });
  const canWrite = can(access, "criminal.refer", "case.create");

  const load = useCallback(() => {
    api.get(`${U}/le/dashboard`).then((r) => setDash(r.data)).catch(() => {});
    api.get(`${U}/le/incidents`, { params: { status, ...(q ? { q } : {}) } }).then((r) => setRows(r.data)).catch(() => {});
    if (canWrite) api.get(`${U}/le/saved-searches`).then((r) => setSearches(r.data)).catch(() => {});
  }, [status, q, canWrite]);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, ok) => {
    try { await fn(); toast.success(ok); load(); }
    catch (e) {
      const d = e.response?.data?.detail;
      if (d?.ibr_errors) d.ibr_errors.forEach((er) => toast.error(`IBR — ${er.field}: ${er.message}`));
      else toast.error(typeof d === "string" ? d : d?.message || "Failed");
    }
  };

  const createIncident = () => run(async () => {
    const persons = [];
    if (f.victim.trim()) persons.push({ name: f.victim.trim(), role: "VICTIM" });
    if (f.suspect.trim()) persons.push({ name: f.suspect.trim(), role: "SUSPECT" });
    const r = await api.post(`${U}/le/incidents`, {
      incident_type: f.incident_type, occurred_at: f.occurred_at, location: f.location,
      narrative: f.narrative, persons, vehicles: f.plate.trim() ? [{ plate: f.plate.trim() }] : [] });
    setF({ ...f, location: "", narrative: "", victim: "", suspect: "", plate: "" });
    if (r.data.ibr_errors?.length) r.data.ibr_errors.forEach((er) => toast.warning(`Edit ${er.edit_number} — ${er.message}`));
    navigate(`/officer/report/${r.data.id}`);
  }, "Field report saved as DRAFT — opening Report Writing");

  const maxType = Math.max(1, ...Object.values(dash?.by_type || { x: 1 }));
  const maxDay = Math.max(1, ...(dash?.week || []).map((d) => d.count));

  return (
    <div className="space-y-4" data-testid="le-records-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900 flex items-center gap-2">
          <Shield className="w-6 h-6 text-ujms" /> Law Enforcement Records
        </h1>
        <p className="text-sm text-slate-500">Capture, process, analyze, and share incident data — field reports, IBR validation, work queues, and saved-search alerts.</p>
      </div>

      {dash && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Kpi label="Draft" value={dash.status_counts.DRAFT} accent="text-amber-400" />
          <Kpi label="Submitted" value={dash.status_counts.SUBMITTED} accent="text-sky-400" />
          <Kpi label="Approved" value={dash.status_counts.APPROVED} accent="text-emerald-400" />
          <Kpi label="Rejected" value={dash.status_counts.REJECTED} accent="text-red-400" />
          <Kpi label="IBR Errors Open" value={dash.ibr_errors_open} accent="text-orange-400" />
        </div>
      )}

      {dash && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <div className={`${dark} p-4`} data-testid="le-chart-types">
            <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-3">Incidents by offense type</div>
            {Object.entries(dash.by_type).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([t, n]) => (
              <div key={t} className="flex items-center gap-2 mb-1.5">
                <span className="w-24 text-[10px] font-mono text-slate-300">{t}</span>
                <div className="flex-1 h-3 bg-[#1a2a45] rounded-sm overflow-hidden">
                  <div className="h-full bg-ujms" style={{ width: `${(n / maxType) * 100}%` }} />
                </div>
                <span className="w-6 text-right text-[10px] font-mono text-slate-300">{n}</span>
              </div>
            ))}
            {!Object.keys(dash.by_type).length && <div className="text-xs text-slate-500">No incidents yet.</div>}
          </div>
          <div className={`${dark} p-4`} data-testid="le-chart-week">
            <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-3">Incidents — last 7 days</div>
            <div className="flex items-end gap-2 h-28">
              {dash.week.map((d) => (
                <div key={d.date} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-[9px] font-mono text-slate-300">{d.count}</span>
                  <div className="w-full bg-ujms rounded-sm" style={{ height: `${(d.count / maxDay) * 88 + 4}px` }} />
                  <span className="text-[8px] font-mono text-slate-500">{d.date.slice(5)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {canWrite && (
        <Panel title="New field report — write anywhere, on any device" testId="le-new-report">
          <div className="p-3 grid grid-cols-2 md:grid-cols-4 gap-2">
            <Field label="Offense type" required>
              <select className={selectCls} value={f.incident_type} onChange={(e) => setF({ ...f, incident_type: e.target.value })} data-testid="le-type-select">
                {(dash?.incident_types || []).map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Occurred on" required>
              <input type="date" className={inputCls} value={f.occurred_at} onChange={(e) => setF({ ...f, occurred_at: e.target.value })} data-testid="le-date-input" />
            </Field>
            <Field label="Location" required>
              <input className={inputCls} value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} placeholder="e.g. 2200 S State St" data-testid="le-location-input" />
            </Field>
            <Field label="Vehicle plate">
              <input className={inputCls} value={f.plate} onChange={(e) => setF({ ...f, plate: e.target.value.toUpperCase() })} placeholder="optional" data-testid="le-plate-input" />
            </Field>
            <Field label="Victim">
              <input className={inputCls} value={f.victim} onChange={(e) => setF({ ...f, victim: e.target.value })} placeholder="name (required for person crimes)" data-testid="le-victim-input" />
            </Field>
            <Field label="Suspect">
              <input className={inputCls} value={f.suspect} onChange={(e) => setF({ ...f, suspect: e.target.value })} placeholder="optional" data-testid="le-suspect-input" />
            </Field>
            <div className="col-span-2">
              <Field label="Narrative (min 20 characters — IBR checked)" required>
                <input className={inputCls} value={f.narrative} onChange={(e) => setF({ ...f, narrative: e.target.value })} placeholder="What happened…" data-testid="le-narrative-input" />
              </Field>
            </div>
          </div>
          <div className="px-3 pb-3">
            <button className={btnPrimary} disabled={!f.location.trim() || !f.narrative.trim()} onClick={createIncident} data-testid="le-create-btn">Save Field Report</button>
          </div>
        </Panel>
      )}

      <Panel title="incident records" testId="le-incidents" action={
        <div className="flex gap-1.5 items-center">
          <Search className="w-3.5 h-3.5 text-slate-400" />
          <input className={`${inputCls} w-52`} placeholder="Search persons, plates, locations…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="le-search-input" />
          <select className={`${selectCls} w-32`} value={status} onChange={(e) => setStatus(e.target.value)} data-testid="le-status-filter">
            {["ALL", "DRAFT", "SUBMITTED", "APPROVED", "REJECTED"].map((s) => <option key={s}>{s}</option>)}
          </select>
        </div>}>
        <table className="w-full border-collapse">
          <thead><tr><th className={thCls}>Incident #</th><th className={thCls}>Type</th><th className={thCls}>Occurred</th>
            <th className={thCls}>Location</th><th className={thCls}>Persons</th><th className={thCls}>Officer</th>
            <th className={thCls}>IBR</th><th className={thCls}>Status</th><th className={thCls}></th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-slate-50" data-testid={`le-row-${r.incident_number}`}>
                <td className={`${tdCls} font-mono text-xs font-semibold`}>
                  <button className="text-ujms-dark hover:underline font-bold" onClick={() => navigate(`/officer/incident/${r.id}`)}
                    data-testid={`le-open-${r.incident_number}`}>{r.incident_number}</button>
                </td>
                <td className={`${tdCls} text-xs`}>{r.incident_type}</td>
                <td className={`${tdCls} font-mono text-xs`}>{(r.occurred_at || "").slice(0, 10)}</td>
                <td className={`${tdCls} text-xs max-w-[180px] truncate`}>{r.location}</td>
                <td className={`${tdCls} text-xs`}>{(r.persons || []).map((p) => `${p.name} (${p.role[0]})`).join(", ") || "—"}</td>
                <td className={`${tdCls} text-xs`}>{r.reporting_officer}</td>
                <td className={tdCls}>{(r.ibr_errors || []).length
                  ? <span className="inline-flex items-center gap-1 text-[10px] font-bold text-orange-700 bg-orange-50 border border-orange-300 rounded-sm px-1.5 py-0.5" title={r.ibr_errors.map((e) => e.message).join("\n")} data-testid={`le-ibr-${r.incident_number}`}><FileWarning className="w-3 h-3" />{r.ibr_errors.length}</span>
                  : <span className="text-[10px] font-bold text-emerald-700">CLEAN</span>}</td>
                <td className={tdCls}><Badge value={r.status} /></td>
                <td className={`${tdCls} whitespace-nowrap`}>
                  {canWrite && ["DRAFT", "REJECTED"].includes(r.status) &&
                    <button className={btnGhost} onClick={() => run(() => api.post(`${U}/le/incidents/${r.id}/submit`), `${r.incident_number} submitted for review`)} data-testid={`le-submit-${r.incident_number}`}>Submit</button>}
                  {canWrite && r.status === "SUBMITTED" && <>
                    <button className={`${btnGhost} ml-1 border-emerald-400 text-emerald-700`} onClick={() => run(() => api.post(`${U}/le/incidents/${r.id}/review`, { action: "APPROVE" }), `${r.incident_number} approved`)} data-testid={`le-approve-${r.incident_number}`}>Approve</button>
                    <button className={`${btnGhost} ml-1 border-red-300 text-red-700`} onClick={() => { const n = window.prompt("Rejection notes (required):"); if (n) run(() => api.post(`${U}/le/incidents/${r.id}/review`, { action: "REJECT", notes: n }), `${r.incident_number} rejected`); }} data-testid={`le-reject-${r.incident_number}`}>Reject</button>
                  </>}
                  {canWrite && r.status === "APPROVED" && !r.arrest_id && (
                    <button className={`${btnGhost} ml-1 border-ujms text-ujms font-semibold`}
                      onClick={() => run(async () => {
                        const res = await api.post(`${U}/le/incidents/${r.id}/convert-to-arrest`);
                        toast.success(`Charge: ${res.data.charge} → prosecutor review`);
                      }, `${r.incident_number} booked as arrest — nothing retyped`)}
                      data-testid={`le-book-${r.incident_number}`}>Book Arrest</button>
                  )}
                  {r.arrest_id && (
                    <span className="ml-1 inline-flex items-center text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-300 rounded-sm px-1.5 py-0.5"
                      title={`Booked ${r.converted_at?.slice(0, 16).replace("T", " ")} by ${r.converted_by}`}
                      data-testid={`le-booked-${r.incident_number}`}>BOOKED ✓</span>
                  )}
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={9} className={tdCls}>No incident records match.</td></tr>}
          </tbody>
        </table>
      </Panel>

      {canWrite && (
        <Panel title="saved searches with notifications — get alerted when new records match" testId="le-saved-searches" action={
          <div className="flex gap-1.5">
            <input className={`${inputCls} w-32`} placeholder="Name" value={ss.name} onChange={(e) => setSs({ ...ss, name: e.target.value })} data-testid="le-ss-name" />
            <input className={`${inputCls} w-44`} placeholder="Match text (name, plate, street…)" value={ss.query} onChange={(e) => setSs({ ...ss, query: e.target.value })} data-testid="le-ss-query" />
            <button className={btnPrimary} disabled={!ss.name.trim() || !ss.query.trim()}
              onClick={() => run(async () => { await api.post(`${U}/le/saved-searches`, ss); setSs({ name: "", query: "" }); }, "Saved search armed — you'll be notified on matches")}
              data-testid="le-ss-create"><BellPlus className="w-3.5 h-3.5" /> Watch</button>
          </div>}>
          <div className="p-3 flex flex-wrap gap-2">
            {searches.map((s) => (
              <span key={s.id} className="inline-flex items-center gap-1.5 text-xs bg-[#f0f5fa] border border-[#c8d4e0] rounded-sm px-2 py-1" data-testid={`le-ss-chip-${s.name}`}>
                <b>{s.name}</b> · “{s.query}”
                <button onClick={() => run(() => api.delete(`${U}/le/saved-searches/${s.id}`), "Saved search removed")} data-testid={`le-ss-del-${s.name}`}><Trash2 className="w-3 h-3 text-red-600" /></button>
              </span>
            ))}
            {!searches.length && <span className="text-xs text-slate-400">No saved searches yet — watch a person, plate, or location and get notified when new incidents match.</span>}
          </div>
        </Panel>
      )}
    </div>
  );
}
